import { LocalizationProvider, DateTimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
} from '@mui/material';
import { useState } from 'react';
import { Artist, CalendarEvent } from '../../@types/api';
import dayjs from 'dayjs';
import { eventTypes } from '../../Constants';

const newEvent: CalendarEvent = {
  artistId: -1,
  startDate: '',
  id: '',
  title: '',
  start: dayjs().hour(20).minute(0).second(0).toDate(), // Set start to today at 8 PM
  end: dayjs().hour(23).minute(59).second(0).toDate(), // Set end to today at 11:59 PM
  artistName: '',
  bookingId: 0,
  color: '',
  comments: '',
  contractNote: '',
  description: '',
  emailAddress: '',
  endDate: '',
  eventTypeId: 0,
  gigId: 0,
  link: '',
  location: '',
  ticketDetail: '',
  venue: '',
  clientName: '',
  telephone: '',
};

interface Props {
  artists: Artist[];
  showAddEvent: boolean;
  setShowAddEvent: (value: boolean) => void;
  addEvent: (event: CalendarEvent) => void;
}
const CreateEventDialogue: React.FC<Props> = ({
  artists,
  showAddEvent,
  setShowAddEvent,
  addEvent,
}) => {
  const [appointmentChanges, setAppointmentChanges] =
    useState<CalendarEvent>(newEvent);
  const changeAppointment = ({
    field,
    changes,
  }: {
    field: string;
    changes: any;
  }) => {
    const nextChanges = {
      ...appointmentChanges,
      [field]: changes,
    };
    setAppointmentChanges(nextChanges);
  };

  const textEditorProps = (field: keyof CalendarEvent) => ({
    onChange: ({ target: change }: React.ChangeEvent<HTMLInputElement>) =>
      changeAppointment({
        field: field,
        changes: change.value,
      }),
    value: appointmentChanges[field] || '',
    label: field[0].toUpperCase() + field.slice(1),
    sx: { mt: 2 },
    fullWidth: true,
  });

  const isEventValid =
    appointmentChanges.artistId && appointmentChanges.eventTypeId;
  return (
    <Dialog open={showAddEvent}>
      <DialogTitle>Add Event</DialogTitle>
      <DialogContent>
        <Box sx={{ minWidth: '552px', pt: 2 }}>
          <TextField
            id='outlined-select-currency'
            select
            label='Select Artist'
            value={appointmentChanges.artistId}
            onChange={(e) => {
              changeAppointment({
                field: 'artistId',
                changes: e.target.value,
              });
            }}
            fullWidth
            size='small'
            variant='outlined'
          >
            {artists?.map((a: Artist) => (
              <MenuItem key={a.artistId} value={a.artistId}>
                {a.artistName}
              </MenuItem>
            ))}
          </TextField>
          <Box mt={2}>
            <LocalizationProvider
              dateAdapter={AdapterDayjs}
              adapterLocale={'en'}
            >
              <DateTimePicker
                label='Start Date'
                value={dayjs(appointmentChanges['start'])}
                onChange={(date) => {
                  const start = date ? date.toDate() : new Date();
                  const end = date ? date.toDate() : new Date();
                  end.setHours(23, 59, 0);
                  const nextChanges: CalendarEvent = {
                    ...appointmentChanges,
                    start: start,
                    end: end,
                  };
                  setAppointmentChanges(nextChanges);
                }}
                format='DD/MM/YYYY h:mm a'
                onError={() => null}
              />
            </LocalizationProvider>
          </Box>
          <TextField
            id='outlined-select-currency'
            select
            label='Event Type'
            value={appointmentChanges.eventTypeId}
            onChange={(e) => {
              changeAppointment({
                field: 'eventTypeId',
                changes: e.target.value,
              });
            }}
            size='small'
            fullWidth
            variant='outlined'
            sx={{ mt: 2 }}
          >
            <MenuItem value={eventTypes.OTHERGIG}>Not Available</MenuItem>
            <MenuItem value={eventTypes.FINNEGANS}>Finnegans Wake</MenuItem>
            <MenuItem value={eventTypes.PUBLICEVENT}>Public Event</MenuItem>
          </TextField>
          {appointmentChanges.eventTypeId === eventTypes.PUBLICEVENT && (
            <>
              <TextField
                {...textEditorProps('location')}
                variant='outlined'
                size='small'
              />
              <TextField
                {...textEditorProps('venue')}
                variant='outlined'
                size='small'
              />
              <TextField
                {...textEditorProps('link')}
                variant='outlined'
                size='small'
              />
              <TextField
                {...textEditorProps('ticketDetail')}
                variant='outlined'
                size='small'
              />
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => addEvent(appointmentChanges)}
          color='primary'
          variant='contained'
          size='small'
          disabled={!isEventValid}
        >
          Create
        </Button>
        <Button
          onClick={() => setShowAddEvent(false)}
          color='primary'
          variant='outlined'
          size='small'
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateEventDialogue;
